"use client";

import { useEffect, useState, useRef, ReactNode } from "react";

interface LazyViewportProps {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: string;
}

export default function LazyViewport({
  children,
  rootMargin = "250px",
  minHeight = "200px",
}: LazyViewportProps) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isBot = /googlebot|bingbot|yandex|baidu|duckduckbot/i.test(ua);

    // Render immediately for search bots
    if (isBot) {
      setIsIntersecting(true);
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.unobserve(el);
        }
      },
      { rootMargin }
    );

    observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [rootMargin]);

  return (
    <div
      ref={containerRef}
      style={{ minHeight: isIntersecting ? "auto" : minHeight }}
    >
      {isIntersecting ? children : null}
    </div>
  );
}
